import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import * as routes from '../../../../constants/routes';
import { resetLegalAdviceQuestionResults, searchLegalAdviceQuestions } from '../../../../redux/actions';
import { combineClassNames } from '../../../../app/functions';
import searchIcon from '../../../../assets/icons/search-icon-white.svg';
import Tag from '../../../../components/common/shared/tag';
import './index.scss';

const HTML_REGEX = /(<([^>]+)>)/ig;

const SEARCH_REQUEST_DELAY = 1.5 * 1000;
const SEARCH_LENGTH_THRESHOLD = 2;
const SEARCH_RESULTS_LIMIT = 5;

const TAG_IDENTIFIER = 'legal-advice-tag';

const QuickSearch = ({ availableTags, onTagClick, onNewQuestionClick }) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const translations = useSelector((store) => store.config.Translations);
    const legalAdvicePage = useSelector((store) => store.data.legalAdvicePage);
    const legalAdviceQuestionResults = useSelector((store) => store.data.legalAdviceQuestionResults);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchQueryWords, setSearchQueryWords] = useState([]);

    const [isWindowVisible, setIsWindowVisible] = useState(false);
    const [areResultsVisible, setAreResultsVisible] = useState(false);

    const [lastTimeout, setLastTimeout] = useState(0);

    const containerRef = useRef(null);

    const handleGlobalClickEvent = (event) => {
        // Keep the window open if the user clicks inside it
        if (containerRef.current && containerRef.current.contains(event.target)) {
            // Unless the user clicks on a tag, and then close it
            if (event.target.classList.contains(TAG_IDENTIFIER)) {
                setIsWindowVisible(false);
                return;
            }

            setIsWindowVisible(true);
        } else {
            setIsWindowVisible(false);
        }
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();
        history.push(routes.legal_advice + routes.legal_advice_routes.search_results + `/${searchQuery}`);
    };

    const handleSearchQueryChange = (event) => {
        const newValue = event.target.value;

        setSearchQuery(newValue);
        setSearchQueryWords(newValue.split(' ').filter(
            (singleWord) => singleWord.length > 1 // Ignore single characters
        ));
    };

    const emphasizeSearchQueryWords = (string) => {
        let finalString = string;

        searchQueryWords.forEach((singleWord) => {
            finalString = finalString.replace(singleWord, `<span class="line-highlighted-text">${singleWord}</span>`);
        });

        return finalString;
    };

    // Register event listeners
    useEffect(() => {
        document.addEventListener('click', handleGlobalClickEvent);
        return () => document.removeEventListener('click', handleGlobalClickEvent);
    }, []);

    // Schedule API request for fetching search results
    useEffect(() => {
        if (lastTimeout) {
            clearTimeout(lastTimeout);
        }

        if (searchQuery.length >= SEARCH_LENGTH_THRESHOLD) {
            setLastTimeout(
                setTimeout(() => {
                    dispatch(searchLegalAdviceQuestions({ query: searchQuery }));
                    setAreResultsVisible(true);
                }, SEARCH_REQUEST_DELAY)
            );
        } else {
            dispatch(resetLegalAdviceQuestionResults());
            setAreResultsVisible(false);
        }
    }, [searchQuery]);

    return (
        <div className="legal-advice-quick-search" ref={containerRef}>
            <form className="search-form" onSubmit={handleFormSubmit}>
                <input
                    type="text"
                    className="search-input"
                    placeholder={translations['legal_advice_quick_search_placeholder']}
                    value={searchQuery}
                    onChange={handleSearchQueryChange}
                    required
                />

                <button
                    type="submit"
                    className="search-button"
                >
                    <img
                        src={searchIcon}
                        alt="Search Icon"
                        className="search-button-icon"
                    />
                </button>
            </form>

            <div
                className={combineClassNames(
                    'search-window',
                    isWindowVisible && 'search-window_visible',
                )}
            >
                {areResultsVisible ? (
                    (legalAdviceQuestionResults && legalAdviceQuestionResults.length > 0) ? (
                        legalAdviceQuestionResults.slice(0, SEARCH_RESULTS_LIMIT).map((questionData) => (
                            <div key={questionData.id} className="search-window-line">
                                <Link
                                    to={routes.legal_advice + routes.legal_advice_routes.view_question + `/${questionData.id}`}
                                    className="line-link line-link_overflow-ellipsis"
                                >
                                    {ReactHtmlParser(
                                        emphasizeSearchQueryWords(
                                            questionData.question_content.replace(HTML_REGEX, '')
                                        )
                                    )}
                                </Link>
                            </div>
                        ))
                    ) : (
                        <div className="search-window-line">
                            <p className="line-paragraph">
                                {translations['legal_advice_quick_search_new_message'] + ' '}
                                <Link
                                    to={routes.legal_advice + routes.legal_advice_routes.new_question}
                                    className="line-link line-link_distinguished"
                                    onClick={onNewQuestionClick}
                                >
                                    {translations['legal_advice_quick_search_new_link']}
                                </Link>
                            </p>
                        </div>
                    )
                ) : (
                    <>
                        <div className="search-window-line">
                            <p className="line-paragraph">
                                {translations['legal_advice_quick_search_suggestion']}
                            </p>
                        </div>

                        <div className="search-window-line">
                            <div className="line-tags-grid">
                                {Object.values(availableTags).map((tagData) => (
                                    <Tag
                                        key={tagData.id}
                                        data={tagData}
                                        clickable
                                        onTagClick={onTagClick}
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default QuickSearch;
